import React, {KeyboardEventHandler, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Indicator} from '../../proto/generated/indicator'
import {Box, Chip, Dialog, DialogContent, InputAdornment, List, ListItem, ListItemButton, ListItemText, styled, TextField, Typography} from '@mui/material'
import SearchIcon from '../../assets/icons/SearchIcon'
import commonActions from '../../actions/common.actions'
import {useDispatch, useSelector} from 'react-redux'
import {MULTIPANE_FULLSCREEN_MODE_ID} from '../../assets/constants'
import {selectMultiPaneFullScreen} from '../../selectors/common'
import ClearIcon from '../../assets/icons/ClearIcon'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import {ExtraSmallButton} from "../custom";
import { useNonModalDialog } from '../common/useNonModalDialog'
import { useUser } from '../../context/UserContext'
import { FeatureCategory, FeatureName, FeatureSubCategory } from '../adminUserRestrictions/Restrictions.constants'

interface IndicatorItemProps {
    onIndicatorClicked: Function
    indicator: Indicator
    focused: boolean
    isLocked: boolean
}

interface IndicatorsModalProps {
    indicators: Indicator[] | undefined
    open: boolean
    onCloseClicked: Function
    onIndicatorClicked: Function
}

function IndicatorItem(props: IndicatorItemProps) {
  const targetRef = useRef<HTMLLIElement>(null)
  useEffect(() => {
    if (props.focused && targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [props.focused])

  return (
        <ListItem dense disablePadding ref={targetRef}>
            <ListItemButton
                selected={props.focused}
                onClick={() => props.onIndicatorClicked(props.indicator)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListItemText>{`${props.indicator.name} (${props.indicator.shortName})`}</ListItemText>
                </Box>
            </ListItemButton>
        </ListItem>
  )
}

const IndicatorsDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        minHeight: '60vh',
        maxHeight: '60vh'
    }
}))

function IndicatorsModal({indicators, ...props}: IndicatorsModalProps) {
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [inputValue, setInputValue] = useState<string>('')
    const [selectedGroup, setSelectedGroup] = useState<string>()
    const [showGroups, setShowGroups] = useState<boolean>(true)
    const [focusedIndex, setFocusedIndex] = useState<number>(NaN)
    const maxItemsFocusable = useRef<number>(0)
    const dispatch = useDispatch()
    const { user: { hasAccess } } = useUser()
    const oiIndicatorsAccess = hasAccess(FeatureCategory.CHART, FeatureSubCategory.INDICATOR, FeatureName.OI_INDICATORS)
    const volumeProfileIndicatorsAccess = hasAccess(FeatureCategory.CHART, FeatureSubCategory.INDICATOR, FeatureName.VOLUME_PROFILE_INDICATORS )

    const indicatorAccessAllowed = useCallback((indicator) => {
        if((indicator.code === 'ANCHOREDVOLUMEPROFILE' || indicator.code === 'FIXEDRANGEVOLUMEPROFILE') && !volumeProfileIndicatorsAccess) {
            return false
        }
        if((indicator.code === 'OI_HISTORICAL_PROFILE' || indicator.code === 'OI_PROFILE' || indicator.code === 'OPEN_INTEREST') && !oiIndicatorsAccess) {
            return false
        }
        return true
    },[oiIndicatorsAccess, volumeProfileIndicatorsAccess])

    const groupToIndicators = useMemo<Map<string, Indicator[]>>(() => {
        const newGrpToIndicators = new Map<string, Indicator[]>()
        indicators?.slice(0)?.sort((a, b) => (a.name < b.name) ? -1 : 1)?.forEach((indicator) => {
            if (newGrpToIndicators.has(indicator.groupName)) {
                newGrpToIndicators.get(indicator.groupName)!!.push(indicator)
            } else {
                newGrpToIndicators.set(indicator.groupName, [indicator])
            }
        })
        return newGrpToIndicators
    }, [indicators])

    useEffect(() => {
        if (!searchTerm || !searchTerm.length) {
            setShowGroups(true)
        } else {
            setShowGroups(false)
        }
    }, [searchTerm])

    const filteredIndicators = useMemo(() => {
        if (!searchTerm || !searchTerm.length) {
            return groupToIndicators.get(selectedGroup ?? '') ?? []
        }
        return Array.from(groupToIndicators.values()).flat().filter((indicator) => {
            const name = indicator.name.toLowerCase()
            const shortName = indicator.shortName.toLowerCase()
            const searchTermLower = searchTerm.toLowerCase()
            return (
                name.includes(searchTermLower) ||
                shortName.includes(searchTermLower) ||
                (name + ' ' + shortName).includes(searchTermLower)
            )
        })
    }, [searchTerm, selectedGroup, groupToIndicators])

    maxItemsFocusable.current = useMemo(() => filteredIndicators.length, [filteredIndicators.length])

    useEffect(() => {
        if (!selectedGroup && groupToIndicators.size > 0) {
            setSelectedGroup(Array.from(groupToIndicators.keys())[0] ?? '')
        }
    }, [groupToIndicators, selectedGroup])

    useEffect(() => {
        setFocusedIndex(NaN)
        showGroups && setInputValue('')
    }, [selectedGroup, showGroups])

    const increaseFocusIndex = useCallback(() => {
        if (Number.isNaN(focusedIndex) || focusedIndex === maxItemsFocusable.current - 1) {
            maxItemsFocusable.current && setFocusedIndex(0)
        } else if (focusedIndex + 1 <= maxItemsFocusable.current - 1) {
            setFocusedIndex(focusedIndex + 1)
        }
    }, [focusedIndex])

    const decreaseFocusIndex = useCallback(() => {
        if (Number.isNaN(focusedIndex) || focusedIndex === 0) {
            maxItemsFocusable.current && setFocusedIndex(maxItemsFocusable.current - 1)
        } else {
            setFocusedIndex(focusedIndex - 1)
        }
    }, [focusedIndex])

    useEffect(() => {
        if (!Number.isNaN(focusedIndex) && focusedIndex >= 0 && focusedIndex < maxItemsFocusable.current) {
            setInputValue(filteredIndicators[focusedIndex].name)
        }
    }, [focusedIndex])

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback((e) => {
        if (e.key === 'ArrowDown') {
            increaseFocusIndex()
            e.preventDefault()
        } else if (e.key === 'ArrowUp') {
            decreaseFocusIndex()
            e.preventDefault()
        }
    }, [increaseFocusIndex, decreaseFocusIndex])

    const onKeyUp: KeyboardEventHandler<HTMLInputElement> = useCallback((e) => {
        if (e.key === 'Enter' && !Number.isNaN(focusedIndex)) {
            const isFocusedIndexValid = (focusedIndex >= 0 && focusedIndex < filteredIndicators.length)
            AnalyticsManager.logEvent("Indicator Selected", {source: 'keyboard', name: filteredIndicators[focusedIndex].name})
            isFocusedIndexValid && props.onIndicatorClicked(filteredIndicators[focusedIndex])
            !isFocusedIndexValid && commonActions.setApiError(dispatch, {
                error: true,
                message: 'Something went wrong',
                errorType: 'error'
            })
        }
    }, [focusedIndex, filteredIndicators])

    const onCloseClicked = useCallback(() => {
        setSearchTerm('')
        setFocusedIndex(NaN)
        setInputValue('')
        props.onCloseClicked()
    }, [props])
    const container = useSelector(selectMultiPaneFullScreen)

    const { dialogProps, paperRef } = useNonModalDialog(props.open, onCloseClicked)

    return (
        <IndicatorsDialog container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                          open={props.open} onClose={onCloseClicked} fullWidth={true} maxWidth={'sm'}
                          {...dialogProps}
                          PaperProps={{ ref: paperRef, sx: { pointerEvents: 'auto' } }}
                          aria-labelledby="indicators-dialog">

            <DraggableDialogTitle sx={{m: 0, p: 2, width: '100%'}} id="customized-dialog-title">
                <Box sx={{display: 'flex', width: '100%', justifyContent: 'space-between'}}>
                    <Typography variant={'h6'}>Indicators</Typography>
                    <ExtraSmallButton color="inherit" onClick={onCloseClicked} aria-label="close">
                        <ClearIcon fontSize={'small'}/>
                    </ExtraSmallButton>
                </Box>
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    position: 'relative',
                    marginBottom: 1
                }}>
                    <TextField
                        variant="standard"
                        fullWidth
                        multiline={false}
                        autoComplete="undefined"
                        placeholder="Search here..."
                        value={inputValue}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{opacity: 0.5}}/>
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchTerm(event.target.value)
                            setInputValue(event.target.value)
                            setFocusedIndex(NaN)
                        }}
                        autoFocus
                        onKeyUp={onKeyUp}
                        onKeyDown={onKeyDown}
                    />
                </Box>
                {
                    showGroups && <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        gap: 1,
                        flexWrap: 'wrap'
                    }}>
                        {Array.from(groupToIndicators.keys())?.map((group) => (
                            <Chip label={group} onClick={() => {
                                AnalyticsManager.logEvent("Indicator Filter Changed", {source: 'indicator-dialog', filter: group})
                                if (group !== selectedGroup) {
                                    setSelectedGroup(group)
                                }
                            }} key={group} variant={(group !== selectedGroup) ? 'outlined' : 'filled'}/>
                        ))}
                    </Box>
                }
            </DraggableDialogTitle>
            <DialogContent sx={{padding: 0}}>
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        ...(filteredIndicators && filteredIndicators.length > 0 ? {overflowY: 'auto'} : {
                            alignItems: 'center',
                            justifyContent: 'center'
                        })
                    }}
                >
                    {
                        (filteredIndicators && filteredIndicators.length > 0) ? (<List disablePadding>
                            {filteredIndicators?.map((indicator, index) => {
                                const isLocked = !hasAccess(FeatureCategory.CHART, FeatureSubCategory.INDICATOR, indicator.code) || !indicatorAccessAllowed(indicator)
                                return <IndicatorItem 
                                    key={indicator.code} 
                                    onIndicatorClicked={props.onIndicatorClicked}
                                    indicator={indicator} 
                                    focused={index === focusedIndex}
                                    isLocked={isLocked}
                                />
                            })}</List>) : <Typography>No indicators match your criteria</Typography>
                    }
                </Box>
            </DialogContent>
        </IndicatorsDialog>
    )
}

export default IndicatorsModal